<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { ref, watch, computed } from 'vue'
import Button from '@/Components/Utils/Button.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { debounce } from 'lodash-es'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import Icon from '@/Components/Utils/Icon.vue'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSearch, faCheck, faCheckCircle } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faSearch, faCheck, faCheckCircle)

const isOpenModal = ref(false)

// Search
const searchQuery = ref('')
const isLoadingOrder = ref(false)
const listOrder = ref<any[]>([])

// Track locked customer once first order is selected
const lockedCustomerId = ref<number | null>(null)
const lockedCustomerName = ref<string | null>(null)

const fetchOrder = async (q = '', customerId: number | null = null) => {
    isLoadingOrder.value = true
    try {
        const params: Record<string, string | number> = { q }
        if (customerId) {
            params.customer_id = customerId
        }
        const { data } = await axios.get(route('json.orders.unpaid'), { params })
        listOrder.value = data.data ?? data
    } catch (error) {
        notify({ title: 'Oops!', text: 'Gagal mencari data Order', type: 'error' })
    } finally {
        isLoadingOrder.value = false
    }
}
const debFetchOrder = debounce((q: string) => fetchOrder(q, lockedCustomerId.value), 350)

watch(searchQuery, (val) => {
    debFetchOrder(val)
})

// Selected orders
const selectedOrderIds = ref<number[]>([])
const selectedOrders = ref<any[]>([])

const toggleOrder = (order: any) => {
    const idx = selectedOrderIds.value.indexOf(order.id)
    if (idx === -1) {
        selectedOrderIds.value.push(order.id)
        selectedOrders.value.push(order)
        // Lock to this customer once first order is selected
        if (!lockedCustomerId.value && order.customer?.id) {
            lockedCustomerId.value = order.customer.id
            lockedCustomerName.value = order.customer.name
            fetchOrder(searchQuery.value, lockedCustomerId.value)
        }
    } else {
        selectedOrderIds.value.splice(idx, 1)
        selectedOrders.value = selectedOrders.value.filter(o => o.id !== order.id)
        // Unlock customer if no orders left
        if (selectedOrderIds.value.length === 0) {
            lockedCustomerId.value = null
            lockedCustomerName.value = null
            fetchOrder(searchQuery.value)
        }
    }
}

const onOpen = () => {
    selectedOrderIds.value = []
    selectedOrders.value = []
    lockedCustomerId.value = null
    lockedCustomerName.value = null
    searchQuery.value = ''
    fetchOrder()
    isOpenModal.value = true
}

// Submit
const isLoadingSubmit = ref(false)
const onSubmitInvoice = () => {
    router.post(
        route('casheer.invoices.store'),
        { orders: selectedOrderIds.value },
        {
            onStart: () => isLoadingSubmit.value = true,
            onFinish: () => isLoadingSubmit.value = false,
            onSuccess: () => {
                selectedOrderIds.value = []
                selectedOrders.value = []
                lockedCustomerId.value = null
                lockedCustomerName.value = null
                isOpenModal.value = false
            },
            onError: () => notify({ title: 'Oops!', text: 'Gagal membuat invoice', type: 'error' }),
        }
    )
}
</script>

<template>
    <div>
        <Button label="Buat Invoice Baru" icon="fas fa-plus" @click="onOpen" />

        <Dialog
            v-model:visible="isOpenModal"
            modal
            header="Buat Invoice Baru"
            :style="{ width: '32rem' }"
            xdismissableMask
        >
            <!-- Customer lock banner -->
            <div v-if="lockedCustomerName" class="mb-3 flex items-center gap-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-700">
                <FontAwesomeIcon icon="fal fa-check" class="text-sky-500 shrink-0" />
                <span>Menampilkan order milik: <span class="font-semibold">{{ lockedCustomerName }}</span></span>
            </div>

            <!-- Search input -->
            <div class="mb-3">
                <label class="block text-sm font-medium mb-1">Cari Order</label>
                <span class="p-input-icon-left w-full relative">
                    <FontAwesomeIcon icon="fal fa-search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <InputText
                        v-model="searchQuery"
                        placeholder="No. order, nama pelanggan, hari, bulan, tahun…"
                        class="w-full !pl-9"
                    />
                </span>
            </div>

            <!-- Order list -->
            <div class="border border-gray-200 rounded-md overflow-y-auto max-h-72 min-h-20">
                <div v-if="isLoadingOrder" class="flex items-center justify-center h-20 text-gray-400">
                    <LoadingIcon />
                </div>
                <div v-else-if="!listOrder.length" class="flex items-center justify-center h-20 text-gray-400 text-sm italic">
                    Tidak ada order yang tersedia
                </div>
                <ul v-else class="divide-y divide-gray-100">
                    <li
                        v-for="order in listOrder"
                        :key="order.id"
                        class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 select-none"
                        :class="selectedOrderIds.includes(order.id) ? 'bg-sky-50' : ''"
                        @click="toggleOrder(order)"
                    >
                        <!-- Checkbox indicator -->
                        <div
                            class="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                            :class="selectedOrderIds.includes(order.id) ? 'bg-sky-500 border-sky-500 text-white' : 'border-gray-300'"
                        >
                            <FontAwesomeIcon v-if="selectedOrderIds.includes(order.id)" icon="fal fa-check" class="text-xs" />
                        </div>

                        <!-- Order info -->
                        <div class="flex-1 min-w-0">
                            <div class="flex justify-between">
                                <div class="flex items-center gap-2">
                                    <span class="font-semibold text-sm">{{ order.reference }}</span>
                                    <Icon v-if="order.type_icon" :data="order.type_icon" class="text-xs" />
                                </div>

                                <div v-if="order.is_manufacture_finished" v-tooltip="'Manufacture selesai'">
                                    <FontAwesomeIcon icon='fal fa-check' class='text-green-500' fixed-width aria-hidden='true' />
                                </div>
                            </div>
                            <div class="text-xs text-gray-500 truncate">
                                <span v-if="order.customer?.name">{{ order.customer.name }}</span>
                                <span v-else class="italic">Tanpa pelanggan</span>
                                <span class="mx-1">·</span>
                                <span>{{ useFormatTime(order.created_at) }}</span>
                                <span class="mx-1">·</span>
                                <span>{{ useFormatCurrency(order.total_amount) }}</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>

            <!-- Selected count -->
            <p class="mt-2 text-xs text-gray-500">
                {{ selectedOrderIds.length }} order dipilih
            </p>

            <div class="flex justify-end gap-2 mt-4">
                <Button label="Batal" type="tertiary" @click="isOpenModal = false" />
                <Button
                    label="Buat Invoice"
                    full
                    :disabled="!selectedOrderIds.length"
                    :loading="isLoadingSubmit"
                    @click="onSubmitInvoice"
                />
            </div>
        </Dialog>
    </div>
</template>
